import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Bus, Users, PlaneLanding, PlaneTakeoff, Coins, Wallet, Search,
  ChevronDown, ChevronRight, FileSpreadsheet, FileText, Printer,
  SlidersHorizontal, Filter, Loader2,
} from "lucide-react";
import styles from "./TransferReport.module.css";
import { useToast } from "../../../context/ToastContext";
import {
  getTransferManifest, getTransferFilterOptions, downloadTransferReportPdf,
} from "../../../api/reports/useReportsApi";
import { formatEUR, formatReportDate, downloadExcel, printHtml, buildFilterLines, filtersToPrintHtml } from "../reportUtils";

const EMPTY_SUMMARY = {
  transfers: 0, travellers: 0, arrivalTransfers: 0, departureTransfers: 0,
  revenue: 0, supplierCost: 0,
};
const TRANSFER_TYPES = ["Arrival", "Departure", "Custom"];
const BOOKING_STATUSES = ["Confirmed", "Cancelled", "Completed"];
const PAGE_SIZES = [25, 50, 100];

const INITIAL_DRAFT = {
  dateFrom: "", dateTo: "", transferType: "", supplier: "", country: "",
  pickupLocation: "", dropoffLocation: "", pickupTime: "", bookingStatus: "Confirmed",
};

const KPI_META = [
  { key: "transfers", label: "Transfers", Icon: Bus, cls: "kpiBlue", fmt: (v) => v },
  { key: "travellers", label: "Travellers", Icon: Users, cls: "kpiGreen", fmt: (v) => v.toLocaleString("en-US") },
  { key: "arrivalTransfers", label: "Arrival Transfers", Icon: PlaneLanding, cls: "kpiPurple", fmt: (v) => v },
  { key: "departureTransfers", label: "Departure Transfers", Icon: PlaneTakeoff, cls: "kpiOrange", fmt: (v) => v },
  { key: "revenue", label: "Revenue", Icon: Coins, cls: "kpiCyan", fmt: (v) => formatEUR(v) },
  { key: "supplierCost", label: "Supplier Cost", Icon: Wallet, cls: "kpiTeal", fmt: (v) => formatEUR(v) },
];

const TypeBadge = ({ type }) => (
  <span className={`${styles.typeBadge} ${styles[`type${type}`] || styles.typeCustom}`}>
    {type || "Custom"}
  </span>
);

const paxDetailsText = (b) =>
  b.travellers && b.travellers.length
    ? b.travellers.map((t) => `${t.name} (${t.type})`).join(", ")
    : "";

const TransferGroup = ({ transfer, open, onToggle }) => (
  <div className={`${styles.transferRow} ${open ? styles.transferRowOpen : ""}`}>
    <button className={styles.transferHead} onClick={onToggle} type="button">
      <span className={styles.tIcon}><Bus size={16} /></span>

      <div className={styles.tRouteWrap}>
        <div className={styles.tRoute}>
          <TypeBadge type={transfer.transferType} />
          <span className={styles.tPlace}>{transfer.pickupLocation || "—"}</span>
          <span className={styles.tArrow}>→</span>
          <span className={styles.tPlace}>{transfer.dropoffLocation || "—"}</span>
        </div>
        <div className={styles.tSubline}>
          <span>{formatReportDate(transfer.pickupDate)}{transfer.pickupTime ? ` · ${transfer.pickupTime}` : ""}</span>
          {transfer.supplierName && <span>· <b>{transfer.supplierName}</b></span>}
          {transfer.supplierReference && <span>· Ref: {transfer.supplierReference}</span>}
        </div>
      </div>

      <div className={styles.tCol}>
        <span className={styles.tColLabel}>Bookings</span>
        <span className={styles.tColValue}>{transfer.bookingsCount}</span>
      </div>
      <div className={styles.tCol}>
        <span className={styles.tColLabel}>Travellers</span>
        <span className={styles.tColValueStrong}>{transfer.travellers}</span>
      </div>
      <div className={styles.tCol}>
        <span className={styles.tColLabel}>Vehicle</span>
        <span className={styles.tColValue}>{transfer.vehicleType || "—"}</span>
      </div>
      <span className={styles.tChevron}>{open ? <ChevronDown size={17} /> : <ChevronRight size={17} />}</span>
    </button>

    {open && (
      <div className={styles.bookingWrap}>
        <table className={styles.bookingTable}>
          <thead>
            <tr>
              <th className={styles.bNo}>#</th>
              <th>Booking No</th>
              <th>Lead Traveller</th>
              <th>Travellers</th>
              <th>Traveller Details</th>
              <th>Transfer Type</th>
              <th>Pickup Time</th>
              <th>Pickup Location</th>
              <th>Drop-off Location</th>
              <th>Flight No.</th>
              <th>Supplier Ref</th>
            </tr>
          </thead>
          <tbody>
            {transfer.bookings.map((b, i) => {
              const details = paxDetailsText(b);
              return (
                <tr key={`${b.bookingReference}-${i}`}>
                  <td className={styles.bNo}>{i + 1}</td>
                  <td className={styles.bRef}>{b.bookingReference}</td>
                  <td className={styles.bName}>{b.leadTraveller}</td>
                  <td className={styles.bCount}>
                    <span className={styles.bCountChip}>{b.travellersCount}</span>
                  </td>
                  <td className={styles.bDetails}>
                    {details || <span className={styles.bMuted}>Lead traveller only</span>}
                  </td>
                  <td><TypeBadge type={b.transferType} /></td>
                  <td>{b.pickupTime || <span className={styles.bMuted}>—</span>}</td>
                  <td>{b.pickupLocation || <span className={styles.bMuted}>—</span>}</td>
                  <td>{b.dropoffLocation || <span className={styles.bMuted}>—</span>}</td>
                  <td>{b.flightNumber || <span className={styles.bMuted}>—</span>}</td>
                  <td className={styles.bRef}>{b.supplierReference || <span className={styles.bMuted}>—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const TransferReport = () => {
  const { showToast } = useToast();

  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [applied, setApplied] = useState(null);
  const [options, setOptions] = useState({ suppliers: [], countries: [], pickupLocations: [], dropoffLocations: [] });
  const [transfers, setTransfers] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [expanded, setExpanded] = useState(() => new Set());

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const runReport = useCallback(async (filters, { announce = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const res = await getTransferManifest(filters);
      if (!res?.success) throw new Error(res?.message || "Failed to generate transfer report");
      const list = Array.isArray(res.data?.transfers) ? res.data.transfers : [];
      setTransfers(list);
      setSummary(res.data?.summary || EMPTY_SUMMARY);
      setApplied(filters);
      setPage(1);
      setExpanded(new Set(list.length ? [list[0].key] : []));
      if (announce) showToast(`Report generated — ${list.length} transfer${list.length === 1 ? "" : "s"}`, "success");
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to generate transfer report";
      setError(msg);
      setTransfers([]);
      setSummary(EMPTY_SUMMARY);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // Dropdown options + an initial report on mount. Guarded against React 18
  // StrictMode's double-invoke.
  const bootRef = useRef(false);
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    getTransferFilterOptions()
      .then((res) => { if (res?.success) setOptions((o) => ({ ...o, ...(res.data || {}) })); })
      .catch(() => { /* dropdowns just stay empty; report still works */ });
    runReport({ ...INITIAL_DRAFT });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = () => runReport({ ...draft }, { announce: true });

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // ── Client-side search over the generated transfers ──
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return transfers;
    return transfers.filter((t) => {
      if (
        String(t.pickupLocation).toLowerCase().includes(q) ||
        String(t.dropoffLocation).toLowerCase().includes(q) ||
        String(t.supplierName).toLowerCase().includes(q) ||
        String(t.supplierReference).toLowerCase().includes(q) ||
        String(t.transferType).toLowerCase().includes(q) ||
        String(t.vehicleType).toLowerCase().includes(q)
      ) return true;
      return t.bookings.some(
        (b) =>
          String(b.bookingReference).toLowerCase().includes(q) ||
          String(b.leadTraveller).toLowerCase().includes(q) ||
          String(b.flightNumber).toLowerCase().includes(q) ||
          (b.travellers || []).some((p) => String(p.name).toLowerCase().includes(q))
      );
    });
  }, [transfers, search]);

  const total = filtered.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const paged = filtered.slice(start, start + pageSize);
  const rangeFrom = total === 0 ? 0 : start + 1;
  const rangeTo = Math.min(start + pageSize, total);

  // ── Exports ──
  const EXPORT_HEADERS = [
    "Transfer Type", "Pickup Date", "Pickup Time", "Pickup Location", "Drop-off Location",
    "Supplier", "Supplier Ref", "Vehicle", "Booking No", "Lead Traveller",
    "Travellers", "Traveller Details", "Flight No.",
  ];
  const exportRows = () =>
    filtered.flatMap((t) =>
      t.bookings.map((b) => [
        t.transferType,
        formatReportDate(t.pickupDate),
        t.pickupTime || "",
        t.pickupLocation || "",
        t.dropoffLocation || "",
        t.supplierName || "",
        b.supplierReference || t.supplierReference || "",
        t.vehicleType || "",
        b.bookingReference || "",
        b.leadTraveller || "",
        b.travellersCount,
        paxDetailsText(b),
        b.flightNumber || "",
      ])
    );

  // Applied criteria + active search, surfaced in every generated export.
  const activeFilterLines = () => {
    const a = applied || draft;
    const supName = options.suppliers.find((s) => String(s.id) === String(a.supplier))?.name || a.supplier;
    const ptLabel = a.pickupTime === "with" ? "With pickup time" : a.pickupTime === "without" ? "Without pickup time" : "";
    return buildFilterLines([
      ["Transfer date from", a.dateFrom ? formatReportDate(a.dateFrom) : ""],
      ["Transfer date to", a.dateTo ? formatReportDate(a.dateTo) : ""],
      ["Transfer type", a.transferType],
      ["Country", a.country],
      ["Pickup location", a.pickupLocation],
      ["Drop-off location", a.dropoffLocation],
      ["Pickup time", ptLabel],
      ["Supplier", a.supplier ? supName : ""],
      ["Booking status", a.bookingStatus],
      ["Search", search],
    ]);
  };

  const handleExcel = () => {
    if (!filtered.length) return showToast("Nothing to export", "error");
    try {
      downloadExcel("transfer-report.xls", EXPORT_HEADERS, exportRows(), "Transfer Report", activeFilterLines());
      showToast("Excel exported", "success");
    } catch {
      showToast("Excel export failed", "error");
    }
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadTransferReportPdf(applied || draft);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "transfer-report.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast("PDF exported", "success");
    } catch (err) {
      showToast(err?.response?.data?.message || "PDF export failed", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    if (!filtered.length) return showToast("Nothing to print", "error");
    const kpis = KPI_META.map(
      (k) => `<div class="kpi"><div class="l">${k.label}</div><div class="v">${k.fmt(summary[k.key] ?? 0)}</div></div>`
    ).join("");
    const sections = filtered
      .map((t) => {
        const rows = t.bookings
          .map((b) => {
            const details = paxDetailsText(b) || "Lead traveller only";
            return `<tr><td>${b.bookingReference}</td><td>${b.leadTraveller}</td><td>${b.travellersCount}</td><td>${details}</td><td>${b.transferType}</td><td>${b.pickupTime || "—"}</td><td>${b.flightNumber || "—"}</td><td>${b.supplierReference || "—"}</td></tr>`;
          })
          .join("");
        return `<h3 style="margin:16px 0 6px">${t.transferType} — ${t.pickupLocation} → ${t.dropoffLocation} — ${formatReportDate(t.pickupDate)}${t.pickupTime ? " " + t.pickupTime : ""} · ${t.supplierName}${t.supplierReference ? " (" + t.supplierReference + ")" : ""}</h3>
          <table><thead><tr><th>Booking No</th><th>Lead Traveller</th><th>Pax</th><th>Traveller Details</th><th>Type</th><th>Pickup</th><th>Flight</th><th>Supplier Ref</th></tr></thead><tbody>${rows}</tbody></table>`;
      })
      .join("");
    const ok = printHtml(
      `<h1>Transfer Report</h1><p class="sub">All transfer bookings with passenger details · Generated ${new Date().toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}</p>${filtersToPrintHtml(activeFilterLines())}<div class="kpis">${kpis}</div>${sections}`,
      "Transfer Report"
    );
    if (!ok) showToast("Please allow pop-ups to print", "error");
  };

  const pageNumbers = useMemo(() => {
    const nums = [];
    const max = pageCount;
    const cur = safePage;
    const win = 5;
    let s = Math.max(1, cur - Math.floor(win / 2));
    const e = Math.min(max, s + win - 1);
    s = Math.max(1, e - win + 1);
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  }, [pageCount, safePage]);

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.pageHeader}>
        <div className={styles.headerIcon}><Bus size={26} /></div>
        <div>
          <h1 className={styles.headerTitle}>Transfer Report</h1>
          <p className={styles.headerSub}>View and export all transfer bookings with passenger details.</p>
        </div>
      </div>

      {/* ── Report Criteria ── */}
      <div className={styles.card}>
        <div className={styles.criteriaHead}><Filter size={16} /> Report Criteria</div>
        <div className={styles.criteriaGrid}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Transfer Date From</span>
            <input type="date" className={styles.input} value={draft.dateFrom} onChange={setField("dateFrom")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Transfer Date To</span>
            <input type="date" className={styles.input} value={draft.dateTo} onChange={setField("dateTo")} />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Transfer Type</span>
            <select className={styles.select} value={draft.transferType} onChange={setField("transferType")}>
              <option value="">All Types</option>
              {TRANSFER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Supplier</span>
            <select className={styles.select} value={draft.supplier} onChange={setField("supplier")}>
              <option value="">All Suppliers</option>
              {options.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Country</span>
            <select className={styles.select} value={draft.country} onChange={setField("country")}>
              <option value="">All Countries</option>
              {options.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Pickup Location</span>
            <input
              className={styles.input}
              list="transfer-pickup-options"
              placeholder="All Locations"
              value={draft.pickupLocation}
              onChange={setField("pickupLocation")}
            />
            <datalist id="transfer-pickup-options">
              {options.pickupLocations.map((l) => <option key={l} value={l} />)}
            </datalist>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Drop-off Location</span>
            <input
              className={styles.input}
              list="transfer-dropoff-options"
              placeholder="All Locations"
              value={draft.dropoffLocation}
              onChange={setField("dropoffLocation")}
            />
            <datalist id="transfer-dropoff-options">
              {options.dropoffLocations.map((l) => <option key={l} value={l} />)}
            </datalist>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Pickup Time</span>
            <select className={styles.select} value={draft.pickupTime} onChange={setField("pickupTime")}>
              <option value="">All</option>
              <option value="with">With pickup time</option>
              <option value="without">Without pickup time</option>
            </select>
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Booking Status</span>
            <select className={styles.select} value={draft.bookingStatus} onChange={setField("bookingStatus")}>
              <option value="">All</option>
              {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <div className={styles.generateWrap}>
            <button className={styles.generateBtn} onClick={handleGenerate} disabled={loading} type="button">
              {loading ? <Loader2 size={16} className={styles.spin} /> : <Search size={16} />}
              Generate Report
            </button>
          </div>
        </div>
      </div>

      {/* ── KPI Summary ── */}
      <div className={styles.kpiGrid}>
        {KPI_META.map(({ key, label, Icon, cls, fmt }) => (
          <div key={key} className={`${styles.kpiCard} ${styles[cls]}`}>
            <span className={styles.kpiIcon}><Icon size={20} /></span>
            <div className={styles.kpiText}>
              <span className={styles.kpiLabel}>{label}</span>
              <span className={styles.kpiValue}>{loading ? "—" : fmt(summary[key] ?? 0)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── Results ── */}
      <div className={styles.card}>
        <div className={styles.resultsHead}>
          <div>
            <div className={styles.resultsTitle}><SlidersHorizontal size={16} /> Results</div>
            <div className={styles.resultsMeta}>
              {loading ? "Loading…" : `Showing ${rangeFrom} to ${rangeTo} of ${total} transfer${total === 1 ? "" : "s"}`}
            </div>
          </div>
          <div className={styles.searchBox}>
            <Search size={15} />
            <input
              className={styles.searchInput}
              placeholder="Search transfer, location, booking, passenger…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </div>

        {error ? (
          <div className={styles.errorBox}>{error}</div>
        ) : loading ? (
          <div className={styles.skeletonWrap}>
            {[...Array(4)].map((_, i) => <div key={i} className={styles.skeletonRow} />)}
          </div>
        ) : paged.length === 0 ? (
          <div className={styles.empty}>
            <Bus size={30} />
            <div className={styles.emptyTitle}>No transfers found</div>
            <div className={styles.emptySub}>Adjust the criteria above and click <b>Generate Report</b>.</div>
          </div>
        ) : (
          <div className={styles.transferList}>
            {paged.map((t) => (
              <TransferGroup key={t.key} transfer={t} open={expanded.has(t.key)} onToggle={() => toggleExpand(t.key)} />
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && total > 0 && (
          <div className={styles.pagination}>
            <div className={styles.rowsPer}>
              <span>Rows per page:</span>
              <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className={styles.rowsSelect}>
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className={styles.pager}>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
              <button className={styles.pageBtn} disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>‹</button>
              {pageNumbers.map((n) => (
                <button key={n} className={`${styles.pageBtn} ${n === safePage ? styles.pageBtnActive : ""}`} onClick={() => setPage(n)}>{n}</button>
              ))}
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(safePage + 1)}>›</button>
              <button className={styles.pageBtn} disabled={safePage === pageCount} onClick={() => setPage(pageCount)}>»</button>
            </div>
            <div className={styles.showingText}>Showing {rangeFrom} to {rangeTo} of {total} transfers</div>
          </div>
        )}
      </div>

      {/* ── Export Report ── */}
      <div className={styles.exportCard}>
        <div className={styles.exportLeft}>
          <div className={styles.exportIcon}><FileText size={18} /></div>
          <div>
            <div className={styles.exportTitle}>Export Transfer Report</div>
            <div className={styles.exportSub}>Export the transfer report in your preferred format.</div>
          </div>
        </div>
        <div className={styles.exportBtns}>
          <button className={`${styles.exportBtn} ${styles.excelBtn}`} onClick={handleExcel} disabled={loading} type="button">
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button className={`${styles.exportBtn} ${styles.pdfBtn}`} onClick={handlePdf} disabled={loading || pdfLoading} type="button">
            {pdfLoading ? <Loader2 size={16} className={styles.spin} /> : <FileText size={16} />} Export PDF
          </button>
          <button className={`${styles.exportBtn} ${styles.printBtn}`} onClick={handlePrint} disabled={loading} type="button">
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferReport;
